/* global globalThis */
import URL from "./apiURL";
import dayjs from 'dayjs';
import { authenticatedFetch } from "../../utils/authenticatedFetch";
import { getValidToken } from "../../utils/getValidToken";
const DEFAULT_ORLT_VIEW = "Corrected Plan";
const DEFAULT_ORLT_VIEW_TYPE = "Absolute";

const WEEK_YEAR_REGEX = /^(\d{1,2})\.(\d{4})$/;

const parseWeekYearValue = (value) => {
  if (value === undefined || value === null) return null;
  const text = String(value).trim();
  const match = new RegExp(WEEK_YEAR_REGEX).exec(text);
  if (!match) return null;

  const week = Number(match[1]);
  const year = Number(match[2]);
  if (Number.isNaN(week) || Number.isNaN(year)) return null;

  return { week, year, token: `${week}.${year}` };
};

const wrapper = (filterValues = []) => {
  const normalizedFilterValues = Array.isArray(filterValues) ? filterValues : [];
  const labelMap = {
    "Factory": "Factory",
    "BG": "BG",
    "SmallC": "SmallC",
    "Format": "Format",
    "Basepack": "Basepack",
    "Year": "Year",
    "Month": "Month",
    "Week": "Week",
    "Site Code": "Site Code",
    "CBU": "CBU",
    "Data UOM": "Data UOM",
    "Plant Type": "Plant Type",
  };

  const filters = {
    BG: null,
    SmallC: null,
    Factory: null,
    Format: null,
    Basepack: null,
    Week: null,
    CalendarWeek: null,
    "Site Code": null,
    "CBU": null,
    "Data UOM": "CLD",
    Year: null,
    Month: null,
    "Plant Type": null,
  };

  normalizedFilterValues.forEach(({ label, selectedData }) => {
    const apiKey = labelMap[label];
    if (apiKey && filters.hasOwnProperty(apiKey)) {
      filters[apiKey] = selectedData?.length ? selectedData : null;
    }
  });

  const selectedWeeks = Array.isArray(filters.Week) ? filters.Week : [];
  if (selectedWeeks.length) {
    const parsedWeekYears = selectedWeeks.map(parseWeekYearValue).filter(Boolean);
    const normalizedWeeks = [...new Set(selectedWeeks.map((value) => {
      const parsed = parseWeekYearValue(value);
      if (parsed) return String(parsed.week);
      const weekNum = Number(value);
      return Number.isNaN(weekNum) ? String(value).trim() : String(weekNum);
    }))];
    const derivedYears = [...new Set(parsedWeekYears.map((item) => String(item.year)))];

    const selectedYears = Array.isArray(filters.Year)
      ? filters.Year
          .map((value) => {
            const parsed = parseWeekYearValue(value);
            if (parsed) return String(parsed.year);
            const yearNum = Number(value);
            return Number.isNaN(yearNum) ? null : String(yearNum);
          })
          .filter((value) => value !== null)
      : [];

    const mergedYears = [...new Set([...selectedYears, ...derivedYears])];
    let calendarWeeks = [...new Set(parsedWeekYears.map((item) => item.token))];

    // If UI sends plain Week + Year (instead of week.year), derive CalendarWeek for chart/filter APIs.
    if (!calendarWeeks.length && mergedYears.length) {
      calendarWeeks = normalizedWeeks.flatMap((week) =>
        mergedYears.map((year) => `${Number(week)}.${year}`)
      );
    }

    filters.Week = normalizedWeeks;
    filters.Year = mergedYears.length ? mergedYears : null;
    filters.CalendarWeek = calendarWeeks.length ? [...new Set(calendarWeeks)] : null;
  }

  return filters;
}

const ensureDefaultYearInRequest = (requestObj, originalFilterValues = []) => {
  const hadYearInUI = Array.isArray(originalFilterValues) && originalFilterValues.some(f => String(f.label).trim() === 'Year' && Array.isArray(f.selectedData) && f.selectedData.length > 0);
  if (!hadYearInUI && requestObj?.filters) {
    const hasYear = requestObj.filters.Year && Array.isArray(requestObj.filters.Year) && requestObj.filters.Year.length > 0;
    if (!hasYear) requestObj.filters.Year = [String(dayjs().year())];
  }
};

const buildOrltRequestPayload = (filterValues = [], view = DEFAULT_ORLT_VIEW, viewType = DEFAULT_ORLT_VIEW_TYPE, extra = {}) => {
  const userInfo = JSON.parse(localStorage.getItem("UserInfo"));
  return {
    view: view || DEFAULT_ORLT_VIEW,
    filters: wrapper(filterValues),
    viewType: viewType || DEFAULT_ORLT_VIEW_TYPE,
    ...extra,
    type: "OR Loss Tree",
    email: userInfo ? userInfo?.user_info?.email : null,
  };
};

const postJson = async (url, body) => {
  return await authenticatedFetch(url, {
    method: "POST",
    body: JSON.stringify(body),
  });
};

const postBlobWithAuth = async (url, body) => {
  const token = await getValidToken();
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let errorMessage = `Error: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData?.message || errorData?.error || errorMessage;
    } catch {
      // Ignore JSON parse error for non-JSON responses.
    }
    throw new Error(errorMessage);
  }

  return await response.blob();
};

export async function orLossKPICardsData(filterValues, view = DEFAULT_ORLT_VIEW, viewType = DEFAULT_ORLT_VIEW_TYPE) {
  const requestObj = buildOrltRequestPayload(filterValues, view, viewType);
  ensureDefaultYearInRequest(requestObj, filterValues);
  try {
    return await postJson(URL.orLossKPICards, requestObj);
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

export async function orHeatMapWeek(factory, loss, filterValues, view, selectionType) {
  const requestObj = buildOrltRequestPayload(filterValues, view, DEFAULT_ORLT_VIEW_TYPE, {
    heat_map_filters: {
      loss: loss === "Loss 1" ? "loss1" : "loss2",
      filter: factory,
    },
    selection_type: selectionType
  });
  ensureDefaultYearInRequest(requestObj, filterValues);

  try {
    return await postJson(URL.orHeatMapWeek, requestObj);
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

export async function orLineFillData(factory, loss, filterValues, view, thresholdValue, selectionType) {
  const requestObj = buildOrltRequestPayload(filterValues, view, DEFAULT_ORLT_VIEW_TYPE, {
    heat_map_filters: {
      loss: loss === "Loss 1" ? "loss1" : "loss2",
      filter: factory,
    },
    threshold: thresholdValue,
    selection_type: selectionType,
  });
  ensureDefaultYearInRequest(requestObj, filterValues);

  try {
    return await postJson(URL.orLineFill, requestObj);
  } catch (error) {
    console.error("Error fetching orLineFill data:", error.message);
    throw error;
  }
}

export async function orHeatMapWeekAllLosses(factory, loss, filterValues, view, selectionType, viewType = 'Week') {
  const requestObj = buildOrltRequestPayload(filterValues, view, DEFAULT_ORLT_VIEW_TYPE, {
    heat_map_filters: {
      loss: loss === "Loss 1" ? "loss1" : "loss2",
      filter: factory,
    },
    view_type: viewType
  });
  ensureDefaultYearInRequest(requestObj, filterValues);

  try {
    return await postJson(URL.orHeatMapAllLosses, requestObj);
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

export async function orLossFilters(filterValues, view, clearFilters = false) {
  const requestObj = buildOrltRequestPayload(Array.isArray(filterValues) ? filterValues : [], view, DEFAULT_ORLT_VIEW_TYPE, { clearFilters });
  // If UI did not supply a Year filter explicitly, do not send the injected default Year
  // so that the server returns the full set of Year options (e.g., 2025 and 2026).
  const hadYearInUI = Array.isArray(filterValues) && filterValues.some(f => String(f.label).trim() === 'Year' && Array.isArray(f.selectedData) && f.selectedData.length > 0);
  if (!hadYearInUI && requestObj?.filters) {
    requestObj.filters.Year = null;
  }
  try {
    return await postJson(URL.orLossFilters, requestObj);
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}


export async function orLossFactoryLevelData(filterValues, view) {
  const requestObj = buildOrltRequestPayload(filterValues, view);
  ensureDefaultYearInRequest(requestObj, filterValues);
  try {
    return await postJson(URL.orLossFactoryLevel, requestObj);
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

export async function orLossBreakdownByBgData(filterValues, view) {
  const requestObj = buildOrltRequestPayload(filterValues, view);
  ensureDefaultYearInRequest(requestObj, filterValues);
  try {
    return await postJson(URL.orLossBreakdownByBg, requestObj);
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

export async function orLossWeeklyTrendData(filterValues, view, selectionType) {
  const requestObj = buildOrltRequestPayload(filterValues, view, DEFAULT_ORLT_VIEW_TYPE, {
    selection_type: selectionType
  });
  ensureDefaultYearInRequest(requestObj, filterValues);
  try {
    return await postJson(URL.orLossWeeklyTrend, requestObj);
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

export async function orLossBucketsData(filterValues, view) {
  const requestObj = buildOrltRequestPayload(filterValues, view);
  ensureDefaultYearInRequest(requestObj, filterValues);
  try {
    return await postJson(URL.orLossBuckets, requestObj);
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

export async function l3lossSave(requestObj) {
  try {
    return await authenticatedFetch(URL.l3LossSaveAPI, {
      method: "POST",
      body: JSON.stringify(requestObj),
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}


export async function l3LossGetResponse(email, view) {
  try {
    return await authenticatedFetch(URL.l3LossGetAPI + '?email=' + email + '&view=' + view, {
      method: "GET",
    });
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}


export async function orHeatMapWeeklyTrendLossTree(factory, loss, filterValues, view, viewType = 'Week') {
  const requestObj = buildOrltRequestPayload(filterValues, view, DEFAULT_ORLT_VIEW_TYPE, {
    heat_map_filters: {
      loss: loss === "Loss 1" ? "loss1" : "loss2",
      filter: factory,
    },
    view_type: viewType
  });
  ensureDefaultYearInRequest(requestObj, filterValues);

  try {
    return await postJson(URL.orLossWeeklyTrendLossTree, requestObj);
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
}

export async function orLossRmTable(view, filterValues = {}) {
  const requestObj = buildOrltRequestPayload(filterValues, view);
  ensureDefaultYearInRequest(requestObj, filterValues);

  try {
    return await postJson(URL.orLossRmTable, requestObj);
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
};

export async function orLossExportToExcel(payload) {
  const requestObj = buildOrltRequestPayload(
    payload.filterValues,
    payload?.view || DEFAULT_ORLT_VIEW,
    payload?.viewType || DEFAULT_ORLT_VIEW_TYPE,
    { isRM_table: payload.isRM_table }
  );
  ensureDefaultYearInRequest(requestObj, payload.filterValues);

  try {
    const blob = await postBlobWithAuth(URL.orLossExportToExcel, requestObj);
    
    // Create a download link and trigger download
    const url = globalThis.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `OR_Loss_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    link.remove();
    globalThis.URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
};

export async function handleWeeklyTrendExport(payload) {
  const requestObj = buildOrltRequestPayload(
    payload.filterValues,
    payload?.view || DEFAULT_ORLT_VIEW,
    payload?.viewType || DEFAULT_ORLT_VIEW_TYPE
  );
  ensureDefaultYearInRequest(requestObj, payload.filterValues);

  try {
    const blob = await postBlobWithAuth(URL.weeklyTrendExport, {
      ...requestObj,
      heatmapFilter: payload.heat_map_filters,
    });
    
    // Create a download link and trigger download
    const url = globalThis.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Weekly_Trend_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    link.remove();
    globalThis.URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
};

export async function handleOrLossHeatmapExport(payload) {
  const requestObj = buildOrltRequestPayload(
    payload.filterValues,
    payload?.view || DEFAULT_ORLT_VIEW,
    payload?.viewType || DEFAULT_ORLT_VIEW_TYPE
  );
  ensureDefaultYearInRequest(requestObj, payload.filterValues);

  try {
    const blob = await postBlobWithAuth(URL.orLossHeatmapExport, {
      ...requestObj,
      heatmapFilter: payload.heat_map_filters,
    });
    
    // Create a download link and trigger download
    const url = globalThis.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `OR_Loss_Heatmap_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    link.remove();
    globalThis.URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    console.error("Error fetching data:", error.message);
    throw error;
  }
};

